import React, { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const dayDiff = (a, b) => {
  if (!a || !b) return 0;
  const s = new Date(a);
  const e = new Date(b);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const ms = e.setHours(0, 0, 0, 0) - s.setHours(0, 0, 0, 0);
  return Math.max(1, Math.ceil(ms / 86400000));
};

export default function SummaryRow({
  show,
  quantity,
  unitLabel,
  variantLabel,
  startDate,
  endDate,
  days,
  lineTotal,
  deposit,
}) {
  const [open, setOpen] = useState(false);

  const safeDays = useMemo(() => {
    const d = Number(days) || dayDiff(startDate, endDate);
    return Math.max(0, d);
  }, [days, startDate, endDate]);

  if (!show) return null;

  return (
    <>
      <Divider sx={{ my: 1.5 }} />

      {/* Compact row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Total
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            ${Number(lineTotal || 0).toFixed(2)}
          </Typography>
          {!!deposit && (
            <Chip
              size="small"
              variant="outlined"
              label={`Deposit $${Number(deposit || 0).toFixed(2)}`}
            />
          )}
        </Stack>

        <IconButton
          size="small"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Hide details" : "Show details"}
          sx={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform .2s ease",
          }}>
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {/* Expanded details */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "120px 1fr", sm: "140px 1fr" },
                  rowGap: 0.75,
                  columnGap: 1.25,
                }}>
                <Typography variant="caption" color="text.secondary">
                  Quantity
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {quantity} {unitLabel}
                  {quantity > 1 ? "s" : ""}
                </Typography>

                {variantLabel && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Variant
                    </Typography>
                    <Typography variant="body2">{variantLabel}</Typography>
                  </>
                )}

                <Typography variant="caption" color="text.secondary">
                  Pickup
                </Typography>
                <Typography variant="body2">{fmtDate(startDate)}</Typography>

                <Typography variant="caption" color="text.secondary">
                  Return
                </Typography>
                <Typography variant="body2">{fmtDate(endDate)}</Typography>

                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2">
                  {safeDays > 0
                    ? `${safeDays} day${safeDays > 1 ? "s" : ""}`
                    : "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  );
}
